/**
 * useBPAFindingsGridConfig
 *
 * Builds the `DataGridConfig` for the BPA Findings grid.
 * All data-fetching is delegated to `useBPAFindings`; this hook
 * only concerns itself with how the data is presented.
 *
 * Column definitions:
 *   ID | Account ID | Integration | Finding | Severity | Scan Status
 *   Status | Benchmark | First Seen | Last Seen | Last Fixed | Actions
 */

import { DataGridConfig } from 'components/DataGrid';
import SummaryCard from 'components/SummaryCard';
import { useAtom, useAtomValue } from 'jotai';
import React, { useMemo } from 'react';

import BPAIntegrationCell from './BPAIntegrationCell';
import BPASeverityDescriptions from './BPASeverityDescriptions';
import RowActions from './RowActions';
import { useBPAFindings } from '../../hooks/useBPAFindings';
import {
  columnKeysBPAAtom,
  DEFAULT_BPA_FINDINGS_COLUMN_KEYS,
  findingsDataAtom,
  orderByAtom,
  sortOrderAtom,
  paginationConfigAtom,
  selectedRowIdsAtom,
} from '../../state';
import { BPAFinding } from '../../types/bpaFindings.types';

const MS_PER_DAY = 1000 * 3600 * 24;
const BPA_FINDING_DESCRIPTION_PREVIEW_LENGTH = 60;

const getBPAFindingDescriptionPreview = (
  description?: string | null,
): string => {
  if (!description) return '—';

  return description.length > BPA_FINDING_DESCRIPTION_PREVIEW_LENGTH
    ? `${description.substring(0, BPA_FINDING_DESCRIPTION_PREVIEW_LENGTH)}...`
    : description;
};

interface DueDateMeta {
  dueDate: Date;
  displayValue: string;
  daysDifference: number;
  isOverdue: boolean;
  isDueWithinTenDays: boolean;
  isDueWithinTwentyDays: boolean;
  tooltipMessage: string;
}

const getDueDateMeta = (dueDateValue?: string | null): DueDateMeta | null => {
  if (!dueDateValue) return null;

  const dueDate = new Date(dueDateValue);
  if (Number.isNaN(dueDate.getTime())) return null;

  const currentDate = new Date();
  const daysDifference =
    (dueDate.getTime() - currentDate.getTime()) / MS_PER_DAY;

  return {
    dueDate,
    displayValue: dueDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }),
    daysDifference,
    isOverdue: dueDate < currentDate,
    isDueWithinTenDays: daysDifference <= 10 && daysDifference >= 0,
    isDueWithinTwentyDays: daysDifference <= 20 && daysDifference > 10,
    tooltipMessage:
      daysDifference > 0
        ? `Due in ${daysDifference.toFixed(1)} days`
        : 'Due date passed',
  };
};

const formatServiceName = (service?: string | null): string => {
  if (!service) return '';

  return service
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(' ');
};

export function useBPAFindingsGridConfig(
  openNotesModal: (rowData: BPAFinding) => void,
  openAgeHistoryModal: (rowData: BPAFinding) => void,
) {
  const { data, loading } = useBPAFindings();

  const listingData = useAtomValue(findingsDataAtom);
  const [selectedRowIds, setSelectedRowIds] = useAtom(selectedRowIdsAtom);
  const [columnKeys, setColumnKeys] = useAtom(columnKeysBPAAtom);
  const [paginationConfig, setPaginationConfig] = useAtom(paginationConfigAtom);
  const [orderBy, setOrderBy] = useAtom(orderByAtom);
  const [sortOrder, setSortOrder] = useAtom(sortOrderAtom);

  const config: DataGridConfig = useMemo(
    () => ({
      rowIdKey: 'id',

      // ── Column definitions ────────────────────────────────────────────────
      columns: [
        {
          key: 'id',
          label: 'ID',
          sortable: true,
          headerTooltip: 'Unique Identifier for the finding in SP360 Database',
          enableCopyToClipboard: true,
        },
        {
          key: 'integration_account_id',
          label: 'Account ID',
          sortable: true,
          headerTooltip: 'Integration account ID',
          enableCopyToClipboard: true,
        },
        {
          key: 'integration_integration_label',
          label: 'Integration',
          headerTooltip: 'Integration name',
          sortable: true,
          render: (row) => <BPAIntegrationCell row={row} />,
        },
        {
          key: 'integration_provider_code',
          label: 'Provider',
          sortable: true,
          headerTooltip: 'Cloud provider name',
        },
        {
          key: 'integration_module_code',
          label: 'Module',
          sortable: true,
          headerTooltip: 'Cloud module name',
        },
        {
          key: 'region',
          label: 'Region',
          sortable: true,
          headerTooltip: 'Cloud region',
        },
        {
          key: 'description',
          label: 'Finding',
          sortable: true,
          // NOTE: auto width for one column to expand automatically
          headerCellStyle: { width: 'auto' },
          headerTooltip:
            'Finding description and associated control description',
          rowCellStyle: { width: 'auto' },
          enableCopyToClipboard: true,
          // Return a React node so the shared DataGrid does not generate a
          // second tooltip for the manually truncated preview. Its raw-string
          // duplicate check fails when descriptions contain HTML characters
          // such as the `>` in T12's `->` finding text.
          overrideContents: (row) => (
            <span>{getBPAFindingDescriptionPreview(row.description)}</span>
          ),
          classRules: [
            {
              className: 'trimmed-text-column',
              ruleFunction: (row) =>
                (row.description?.length ?? 0) >
                BPA_FINDING_DESCRIPTION_PREVIEW_LENGTH,
            },
          ],
          tooltip: (row) => (
            <>
              <strong>Finding:</strong>&nbsp;&nbsp;
              {row.description || '—'}
              <br />
              <strong>Control:</strong>&nbsp;&nbsp;
              {row.control_description || '—'}
            </>
          ),
        },
        {
          key: 'control_id',
          label: 'Control ID',
          sortable: true,
          headerTooltip: 'Best practice control ID',
          enableCopyToClipboard: true,
          overrideContents: (row) => row.control_id ?? '',
          classRules: [
            {
              className: 'text-muted fst-italic',
              ruleFunction: (row) => row.control_id == null,
            },
          ],
        },
        {
          key: 'control_service',
          label: 'Service',
          sortable: true,
          headerTooltip: 'Cloud service associated with the control',
          enableCopyToClipboard: true,
          valueToCopy: (row) => row.control_service || '',
          overrideContents: (row) => formatServiceName(row.control_service),
          classRules: [
            {
              className: 'text-muted fst-italic',
              ruleFunction: (row) => !row.control_service,
            },
          ],
        },
        {
          key: 'control_severity',
          label: 'Severity',
          orderBy: 'control_severity_number',
          sortable: true,
          headerTooltip: <BPASeverityDescriptions />,
          render: (row) => {
            const colorClass: Record<string, string> = {
              Critical: 'text-critical',
              High: 'text-serious',
              Medium: 'text-medium',
              Low: 'text-minimal',
            };
            return (
              <span className={colorClass[row.control_severity] || ''}>
                {row.control_severity || '—'}
              </span>
            );
          },
        },
        {
          key: 'scan_status',
          label: 'Status',
          sortable: true,
          headerTooltip: 'Status (Active/Fixed/Ignored)',
          render: (row) => {
            const scanStatus = row.scan_status?.toUpperCase() || '—';
            const isIgnored = row.user_status === 'Ignored';

            return (
              <span>
                {isIgnored ? 'IGNORED' : scanStatus}
                {isIgnored && (
                  <div className="text-muted small">{scanStatus}</div>
                )}
              </span>
            );
          },
        },
        {
          key: 'age',
          label: 'Age',
          sortable: true,
          headerTooltip: 'Latest finding lifecycle age in days',
          overrideContents: (row) => (
            <button
              type="button"
              aria-label={`View age history for finding ${row.id}`}
              onClick={() => openAgeHistoryModal(row)}
              style={{
                background: 'none',
                border: 0,
                color: 'inherit',
                cursor: 'pointer',
                font: 'inherit',
                padding: 0,
              }}
            >
              <u>
                <span style={{ fontWeight: 'bolder' }}>
                  {row.age == null ? '...' : row.age.toFixed(1)}
                </span>
              </u>
              {row.lifecycle_count > 1 && <sup>*{row.lifecycle_count}</sup>}
            </button>
          ),
          tooltip: () => "Click to view the finding's age history",
          classRules: [
            {
              className: 'text-muted fst-italic',
              ruleFunction: (row) => row.age == null,
            },
          ],
        },
        {
          key: 'assignee_name',
          label: 'Assignee',
          sortable: true,
          headerTooltip: 'User assigned to remediate this BPA finding',
          enableCopyToClipboard: true,
          valueToCopy: (row) => row.assignee_email || '',
          classRules: [
            {
              className: 'text-muted fst-italic',
              ruleFunction: (row) => !row.assignee_name,
            },
          ],
          overrideContents: (row) => row.assignee_name || '',
          tooltip: (row) =>
            row.assignee_name ? (
              <SummaryCard
                heading={row.assignee_name}
                data={[
                  { key: 'Email', value: row.assignee_email },
                  { key: 'Id', value: row.assignee_user_id },
                ]}
                description="Assigned user for this BPA finding in SP360"
                icon={
                  <i
                    className="fa fa-user"
                    aria-hidden="true"
                    style={{ fontSize: '3rem' }}
                  ></i>
                }
                mode="tooltip"
              />
            ) : undefined,
        },
        {
          key: 'due_date',
          label: 'Due Date',
          sortable: true,
          headerTooltip: 'Assignment due date',
          overrideContents: (row) =>
            getDueDateMeta(row.due_date)?.displayValue || '',
          classRules: [
            {
              className: 'text-urgent',
              ruleFunction: (row) =>
                getDueDateMeta(row.due_date)?.isOverdue ?? false,
            },
            {
              className: 'text-serious',
              ruleFunction: (row) =>
                getDueDateMeta(row.due_date)?.isDueWithinTenDays ?? false,
            },
            {
              className: 'text-medium',
              ruleFunction: (row) =>
                getDueDateMeta(row.due_date)?.isDueWithinTwentyDays ?? false,
            },
          ],
          tooltip: (row) => {
            const dueDateMeta = getDueDateMeta(row.due_date);
            if (!dueDateMeta) return undefined;

            return (
              <>
                <strong>Due Date: </strong>
                {dueDateMeta.dueDate.toLocaleString()}
                <br />
                {dueDateMeta.tooltipMessage}
              </>
            );
          },
        },
        {
          key: 'benchmark_name',
          label: 'Benchmark',
          sortable: true,
          headerTooltip: 'Benchmark name',
          enableCopyToClipboard: true,
        },
        {
          key: 'first_seen_at',
          label: 'First Detected Date',
          sortable: true,
          headerTooltip: 'First detected date',
          tooltip: (row) => {
            const firstSeenAtDate = new Date(row.first_seen_at);
            const currentDate = new Date();
            const timeDifference =
              currentDate.getTime() - firstSeenAtDate.getTime();
            const daysDifference = Math.ceil(
              timeDifference / (1000 * 3600 * 24),
            );
            return daysDifference > 0
              ? `First detected ${daysDifference} days ago`
              : 'First detected today';
          },
        },
        {
          key: 'last_seen_at',
          label: 'Last Detected Date',
          sortable: true,
          headerTooltip: 'Last detected date',
          tooltip: (row) => {
            const lastSeenAtDate = new Date(row.last_seen_at);
            const currentDate = new Date();
            const timeDifference =
              currentDate.getTime() - lastSeenAtDate.getTime();
            const daysDifference = Math.ceil(
              timeDifference / (1000 * 3600 * 24),
            );
            return daysDifference > 0
              ? `Last detected ${daysDifference} days ago`
              : 'Last detected today';
          },
        },
        {
          key: 'last_fixed_at',
          label: 'Last Fixed Date',
          sortable: true,
          headerTooltip: 'Last fixed date',
          tooltip: (row) => {
            if (!row.last_fixed_at) {
              return 'Not yet fixed';
            }
            const lastFixedAtDate = new Date(row.last_fixed_at);
            const currentDate = new Date();
            const timeDifference =
              currentDate.getTime() - lastFixedAtDate.getTime();
            const daysDifference = Math.ceil(
              timeDifference / (1000 * 3600 * 24),
            );
            return daysDifference > 0
              ? `Last fixed ${daysDifference} days ago`
              : 'Last fixed today';
          },
        },
        {
          key: 'created_at',
          label: 'Created At',
          sortable: true,
          headerTooltip: 'Created date',
          tooltip: (row) => {
            const lastFixedAtDate = new Date(row.last_fixed_at);
            const currentDate = new Date();
            const timeDifference =
              currentDate.getTime() - lastFixedAtDate.getTime();
            const daysDifference = Math.ceil(
              timeDifference / (1000 * 3600 * 24),
            );
            return daysDifference > 0
              ? `Last created ${daysDifference} days ago`
              : 'Last created today';
          },
        },
        {
          key: 'actions',
          label: 'Actions',
          sortable: false,
          render: (row) => (
            <RowActions rowData={row} openNotesModal={openNotesModal} />
          ),
          headerCellStyle: {
            textAlign: 'right',
            maxWidth: 'auto',
            minWidth: '150px !important',
          },
          rowCellStyle: {
            textAlign: 'right',
            minWidth: '150px !important',
            maxWidth: 'auto',
            whiteSpace: 'nowrap',
          },
        },
      ],

      // ── Pagination ────────────────────────────────────────────────────────
      paginationConfig: {
        total_count: listingData.count,
        // DataGrid uses 0-based page numbers internally
        pageNumber: paginationConfig.page - 1,
        pageSize: paginationConfig.pageSize,
        onPageChange: (newPage) =>
          setPaginationConfig({ ...paginationConfig, page: newPage + 1 }),
        onPageSizeChange: (pageSize) =>
          setPaginationConfig({ page: 1, pageSize }),
      },

      // ── Sorting ───────────────────────────────────────────────────────────
      sortingConfig: {
        orderBy: orderBy,
        order: sortOrder,
        onOrderByChange: (column) => setOrderBy(column),
        onOrderChange: (order) => setSortOrder(order as 'asc' | 'desc'),
      },

      // ── Row selection ─────────────────────────────────────────────────────
      rowSelectionConfig: {
        enableSelectionAcrossPages: true,
        preSelectedRows: selectedRowIds,
        onSelectionChange: setSelectedRowIds,
      },

      // ── Column visibility ─────────────────────────────────────────────────
      columnsDisplayConfig: {
        enabledColumnKeys: columnKeys,
        defaultColumnKeys: DEFAULT_BPA_FINDINGS_COLUMN_KEYS,
        onColumnsDisplayChange: setColumnKeys,
      },
    }),
    [
      listingData.count,
      paginationConfig,
      setPaginationConfig,
      orderBy,
      setOrderBy,
      sortOrder,
      setSortOrder,
      selectedRowIds,
      setSelectedRowIds,
      columnKeys,
      setColumnKeys,
      openNotesModal,
      openAgeHistoryModal,
    ],
  );

  return { config, data, loading };
}
